import type { ReactNode } from 'react'

import { Badge } from '@/components/ui/badge'
import { displayableAnalysisHeadline } from '@/lib/ranking-vocabulary'
import { cn } from '@/lib/utils'

/** Shared person summary body for Invite, Analysis, and Tracking cards. */
export type PersonListCardSummaryFields = {
  analysisHeadline?: string | null
  description?: string | null
  skills?: string[]
}

export type PersonListCardSummaryProps = PersonListCardSummaryFields & {
  maxSkills?: number
  /** Pipeline / applied metadata rendered below summary content. */
  metadata?: ReactNode
  className?: string
}

export function PersonListCardSummary({
  analysisHeadline,
  description,
  skills = [],
  maxSkills = 5,
  metadata,
  className,
}: PersonListCardSummaryProps) {
  const trimmedAnalysis = displayableAnalysisHeadline(analysisHeadline)
  const trimmedDescription = description?.trim()
  const visibleSkills = skills.filter((skill) => skill.trim()).slice(0, maxSkills)

  if (!trimmedAnalysis && !trimmedDescription && visibleSkills.length === 0 && !metadata) {
    return null
  }

  return (
    <div className={cn('space-y-2', className)}>
      {trimmedAnalysis ? (
        <p className="text-sm font-medium">{trimmedAnalysis}</p>
      ) : null}
      {trimmedDescription ? (
        <p className="text-sm text-muted-foreground">{trimmedDescription}</p>
      ) : null}
      {visibleSkills.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {visibleSkills.map((skill) => (
            <Badge key={skill} variant="secondary" className="font-normal">
              {skill}
            </Badge>
          ))}
        </div>
      ) : null}
      {metadata ? <div>{metadata}</div> : null}
    </div>
  )
}
